import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  Users, 
  User, 
  Flag, 
  RefreshCcw, 
  Mail, 
  Trash2, 
  ShieldAlert, 
  ShieldCheck,
  Search,
  MoreVertical,
  Plus,
  Loader2,
  Check,
  X,
  Eye,
  EyeOff
} from "lucide-react";
import { useStreams } from "../hooks/useStreams";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { useTranslation } from "react-i18next";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";

interface Contact {
  contactId: number;
  contactUid: string;
  contactName: string;
  contactEmail: string;
  isFlagged: boolean;
  createdAt?: string;
}

interface StreamData {
  streamName: string;
  uid: string;
}

const API_BASE_URL = "https://api.deafassistant.com/contact";
const POLL_INTERVALS = {
  FAST: 1000,
  MEDIUM: 4000,
  SLOW: 8000,
} as const;

const NetworkingPage: React.FC = () => {
  const { t } = useTranslation();
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [streamData, setStreamData] = useState<StreamData | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string>("");
  const [pollingStatus, setPollingStatus] = useState<string>("Initializing...");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [isDeleting, setIsDeleting] = useState<Set<number>>(new Set());
  const [isFlagging, setIsFlagging] = useState<Set<number>>(new Set());
  const [isClearingAll, setIsClearingAll] = useState(false);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newContactIds, setNewContactIds] = useState<Set<number>>(new Set());

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const knownContactIdsRef = useRef<Set<number>>(new Set());
  const isMountedRef = useRef(true);
  const currentPollIntervalRef = useRef<number>(POLL_INTERVALS.FAST);

  const updatePollInterval = useCallback((newInterval: number) => {
    currentPollIntervalRef.current = newInterval;
  }, []);

  const { selected, loading: streamsLoading } = useStreams();
  useEffect(() => {
    if (!user || streamsLoading) return;
    if (selected) {
      setStreamData({ streamName: selected.name, uid: user.uid });
      setError("");
    } else {
      setError("No data available for this user.");
      setIsInitialLoad(false); // no stream, so nothing will be fetched to clear the spinner
    }
  }, [user, selected, streamsLoading]);

  const clearPollingInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const updatePollingStatus = useCallback(() => {
    setPollingStatus(`Syncing every ${currentPollIntervalRef.current / 1000}s`);
  }, []);

  const fetchContacts = useCallback(async () => {
    if (!streamData?.streamName || !streamData?.uid || !isMountedRef.current) return;

    setIsRefreshing(true);

    try {
      const url = new URL(`${API_BASE_URL}/GetStreamContacts`);
      url.search = new URLSearchParams({
        streamName: streamData.streamName,
        streamUid: streamData.uid, 
      }).toString();

      const response = await fetch(url);
      if (!response.ok) throw new Error(`API Error: ${response.status}`);

      const newContacts: Contact[] = await response.json();
      if (!isMountedRef.current) return;

      const currentIds = new Set(newContacts.map((c) => c.contactId));
      const previousIds = knownContactIdsRef.current;
      const newIds = new Set(Array.from(currentIds).filter((id) => !previousIds.has(id)));

      if (newIds.size > 0) {
        setNewContactIds(newIds);
        setTimeout(() => setNewContactIds(new Set()), 2000);
      }

      const hasNewData = newContacts.length > knownContactIdsRef.current.size;
      setContacts(newContacts);
      knownContactIdsRef.current = currentIds;

      if (hasNewData) updatePollInterval(POLL_INTERVALS.FAST);
      else if (currentPollIntervalRef.current === POLL_INTERVALS.FAST) updatePollInterval(POLL_INTERVALS.MEDIUM);
      else updatePollInterval(POLL_INTERVALS.SLOW);

      setError("");
      updatePollingStatus();
      if (isInitialLoad) setIsInitialLoad(false);
    } catch (err) {
      console.error("Polling failed:", err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Connection lost");
        setPollingStatus("Disconnected");
        if (isInitialLoad) setIsInitialLoad(false);
      }
    } finally {
      setTimeout(() => setIsRefreshing(false), 300);
    }
  }, [streamData, isInitialLoad, updatePollingStatus, updatePollInterval]);

  const scheduleNextPoll = useCallback(() => {
    clearPollingInterval();
    intervalRef.current = setTimeout(() => fetchContacts(), currentPollIntervalRef.current);
    updatePollingStatus();
  }, [fetchContacts, clearPollingInterval, updatePollingStatus]);

  useEffect(() => {
    if (!streamData || error) {
      clearPollingInterval();
      return;
    }
    fetchContacts();
    return () => clearPollingInterval();
  }, [streamData, error, fetchContacts, clearPollingInterval]);

  useEffect(() => {
    if (!isInitialLoad && !error && streamData) scheduleNextPoll();
  }, [contacts, isInitialLoad, error, streamData, scheduleNextPoll]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  const handleToggleFlag = useCallback(async (contact: Contact) => {
    if (!streamData) return;
    setIsFlagging((prev) => new Set(prev).add(contact.contactId));

    try {
      let url: URL;
      if (contact.isFlagged) {
        url = new URL(`${API_BASE_URL}/UnflagContact`);
        url.search = new URLSearchParams({
          streamName: streamData.streamName,
          contactUid: contact.contactUid,
          streamUid: streamData.uid,
        }).toString();
      } else {
        url = new URL(`${API_BASE_URL}/FlagContact`);
        url.search = new URLSearchParams({
          streamName: streamData.streamName,
          id: contact.contactId.toString(),
        }).toString();
      }

      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("Flag action failed");

      setContacts((prev) =>
        prev.map((c) => c.contactId === contact.contactId ? { ...c, isFlagged: !c.isFlagged } : c)
      );
    } catch (err) {
      console.error("Failed to toggle flag:", err);
      alert("Action failed.");
    } finally {
      setIsFlagging((prev) => {
        const newSet = new Set(prev);
        newSet.delete(contact.contactId);
        return newSet;
      });
    }
  }, [streamData]);

  const handleDeleteContact = useCallback(async (contact: Contact) => {
    if (!streamData) return;
    if (!window.confirm("Are you sure you want to delete this contact?")) return;

    setIsDeleting((prev) => new Set(prev).add(contact.contactId));

    const url = new URL(`${API_BASE_URL}/DelContact`);
    url.search = new URLSearchParams({
      streamName: streamData.streamName,
      streamUid: streamData.uid,
      userUid: contact.contactUid,
    }).toString();

    try {
      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("Delete failed");
      setContacts((prev) => prev.filter((c) => c.contactId !== contact.contactId));
      knownContactIdsRef.current.delete(contact.contactId);
    } catch (err) {
      console.error("Failed to delete contact:", err);
      alert("Action failed.");
    } finally {
      setIsDeleting((prev) => {
        const newSet = new Set(prev);
        newSet.delete(contact.contactId);
        return newSet;
      });
    }
  }, [streamData]);

  const handleClearAll = useCallback(async () => {
    if (!streamData) return;
    if (!window.confirm("Are you sure you want to clear ALL contacts?")) return;

    setIsClearingAll(true);
    const url = new URL(`${API_BASE_URL}/DelStreamContacts`);
    url.search = new URLSearchParams({
      streamName: streamData.streamName,
      uid: streamData.uid,
    }).toString();

    try {
      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("Clear failed");
      setContacts([]);
      knownContactIdsRef.current.clear();
    } catch (err) {
      console.error("Failed to clear contacts:", err);
      alert("Action failed.");
    } finally {
      setIsClearingAll(false);
    }
  }, [streamData]);

  const filteredContacts = contacts.filter(c => 
    c.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.contactEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading || isInitialLoad) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="networking" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            
            <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                  {t("Networking Contacts")}
                </h1>
                <div className="flex items-center gap-4 mt-2">
                  <p className="text-slate-500 text-lg">
                    Manage leads and connections from <span className="font-bold text-slate-700">{streamData?.streamName}</span>
                  </p>
                  <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-slate-100 shadow-sm">
                    <RefreshCcw size={14} className={`text-blue-500 ${isRefreshing ? "animate-spin" : ""}`} />
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{pollingStatus}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                {contacts.length > 0 && (
                  <button onClick={handleClearAll} disabled={isClearingAll} className="btn-danger flex items-center gap-2 group">
                    {isClearingAll ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} className="text-red-400 group-hover:text-red-600 transition-colors" />}
                    {t("networking.clearAll")}
                  </button>
                )}
              </div>
            </header>

            {/* Search Bar */}
            <div className="bg-white rounded-3xl p-4 shadow-sm border border-slate-100">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 pl-12 pr-4 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Contacts Grid */}
            {filteredContacts.length === 0 ? (
              <div className="bg-white rounded-[3rem] p-20 text-center shadow-sm border border-slate-100">
                <div className="w-24 h-24 bg-blue-50 rounded-[2.5rem] flex items-center justify-center text-blue-300 mx-auto mb-6">
                  <Users size={48} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">No contacts found</h3>
                <p className="text-slate-500 max-w-sm mx-auto">
                  {searchQuery ? "Try refining your search keywords." : "When attendees share their info during your stream, they'll appear here."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredContacts.map((contact, index) => {
                  const isNew = newContactIds.has(contact.contactId);
                  const isBeingDeleted = isDeleting.has(contact.contactId);
                  const isBeingFlagged = isFlagging.has(contact.contactId);

                  return (
                    <div
                      key={contact.contactId}
                      className={`
                        group bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 hover:shadow-xl hover:border-blue-100 transition-all duration-500
                        ${isBeingDeleted ? "opacity-40 scale-95 pointer-events-none" : "opacity-100 scale-100"}
                        ${contact.isFlagged ? "bg-slate-50/50" : ""}
                        ${isNew ? "ring-2 ring-blue-500 ring-offset-4" : ""}
                      `}
                    >
                      <div className="flex justify-between items-start mb-6">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
                          <User size={28} />
                        </div>
                        <div className="flex gap-1">
                          <button 
                            onClick={() => handleToggleFlag(contact)}
                            disabled={isBeingFlagged}
                            className={`p-2 rounded-xl transition-all ${
                              contact.isFlagged 
                              ? "bg-orange-50 text-orange-500 hover:bg-orange-100" 
                              : "bg-slate-50 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                            }`}
                            title={contact.isFlagged ? "Make visible" : "Hide from public"}
                          >
                            {isBeingFlagged ? <Loader2 size={18} className="animate-spin" /> : (contact.isFlagged ? <EyeOff size={18} /> : <Eye size={18} />)}
                          </button>
                          <button 
                            onClick={() => handleDeleteContact(contact)}
                            disabled={isBeingDeleted}
                            className="p-2 bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all"
                            title="Delete contact"
                          >
                            {isBeingDeleted ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900 line-clamp-1 group-hover:text-blue-600 transition-colors" title={contact.contactName}>
                            {contact.contactName}
                          </h3>
                          <div className="flex items-center gap-2 mt-1 text-slate-500 min-w-0">
                            <Mail size={14} className="shrink-0" />
                            <span className="text-sm font-medium truncate" title={contact.contactEmail}>
                              {contact.contactEmail}
                            </span>
                          </div>
                        </div>

                        {contact.isFlagged && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 text-orange-700 rounded-xl text-xs font-bold uppercase tracking-wider">
                            <ShieldAlert size={14} />
                            Hidden from stream
                          </div>
                        )}
                        
                        <div className="pt-4 flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-widest border-t border-slate-50">
                          <span>Contact ID</span>
                          <span className="bg-slate-50 px-2 py-1 rounded-lg">#{contact.contactId}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default NetworkingPage;